import type { ReactElement, ReactNode } from 'react';
import { memo } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'design-system';

import { cn } from '@/lib/utils';

type TooltipVariant = 'default' | 'secondary' | 'plain';

interface CustomTooltipProps {
  title: ReactNode;
  children: ReactElement;
  variant?: TooltipVariant;
  className?: string;
  disableFocusListener?: boolean;
  disableHoverListener?: boolean;
  disableTouchListener?: boolean;
}

/** Arrow colour is set via `[data-slot=tooltip-arrow]` since TooltipContent renders it internally. */
const variantClassName: Record<TooltipVariant, string> = {
  default:
    'bg-gx-highlight text-gx-text [&>[data-slot=tooltip-arrow]]:bg-gx-highlight [&>[data-slot=tooltip-arrow]]:fill-gx-highlight',
  secondary:
    'border-l border-l-gx-highlight bg-gx-response text-gx-text [&>[data-slot=tooltip-arrow]]:bg-gx-response [&>[data-slot=tooltip-arrow]]:fill-gx-response',
  // The neutral tooltip — no arrow, no brand colours, design-system's own default look.
  plain: '[&>[data-slot=tooltip-arrow]]:hidden'
};

const CustomTooltip = ({
  children,
  variant = 'default',
  title,
  className,
  disableFocusListener,
  disableHoverListener,
  disableTouchListener
}: CustomTooltipProps) => {
  const disabled =
    !title || Boolean(disableFocusListener || disableHoverListener || disableTouchListener);

  return (
    <Tooltip disabled={disabled}>
      <TooltipTrigger
        // MUI's Tooltip set aria-label from a string `title`, giving unlabelled
        // triggers (icon buttons, drag handles) an accessible name. base-ui only
        // wires up aria-describedby, so replicate that fallback here.
        aria-label={typeof title === 'string' ? title : undefined}
        delay={150}
        render={children}
      />
      {!disabled && (
        <TooltipContent className={cn(variantClassName[variant], className)}>
          {title}
        </TooltipContent>
      )}
    </Tooltip>
  );
};

export default memo(CustomTooltip);
